import { createStrictContext } from 'utils/createContext'
import { Issue, IssueTrackerContext } from './types'
import {
  useEditIssueMutation,
  useIssueTrackerIssuesQuery,
  useIssueTrackerRelationsQuery,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useGlobalState } from 'context/GlobalStateContext'
import { useCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { useExternalLinks } from 'components/CIMSTable/common/hooks/useExternalLinks'
import { CIMSPage, SharePointParentFolder } from 'shared'
import { useEffect, useState } from 'react'
import { useReportURLDependencies } from 'hooks/useReportURLDependencies'
import { toReportExternalLink } from './utils'
import { getReportID } from '../utils'
import { ProminentLink } from 'components/CIMSTable/common/hooks/useExternalLinks/types'
import { WithPayItemGlobalId } from 'components/CIMSTable/components/HeaderActionButtons/types'
import { simpleEditNotification } from '../../../../../../../../hooks/notificationHandler'

const { Provider, useContextFn } = createStrictContext<IssueTrackerContext>(
  'IssueTrackerContext'
)

export const useIssueTrackerContext = useContextFn

const IssueTrackerProvider: React.FC<WithPayItemGlobalId> = ({
  children,
  payItemGlobalId,
}) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    data: issuesData,
    loading: issuesLoading,
    refetch: refetchIssues,
  } = useIssueTrackerIssuesQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: {
      input: { projectId: { equals: projectId } },
      projectId: projectId!,
    },
  })
  const {
    data: relationsData,
    loading: relationsLoading,
    refetch: refetchRelations,
  } = useIssueTrackerRelationsQuery({
    ...DefaultQueryHookOptions,
    skip: !issuesData?.issues?.records || !projectId,
    variables: {
      projectId: projectId!,
      issueIds: issuesData?.issues?.records?.map(({ id }) => id) ?? [],
    },
  })
  const [issues, setIssues] = useState(Array<Issue>())
  useEffect(() => {
    const comments = relationsData?.issueComments?.records?.reduce(
      (map, { issueId, ...comment }) =>
        map.set(issueId, [...(map.get(issueId) ?? []), comment]),
      new Map<string, Issue['comments']>()
    )
    const payItems = relationsData?.issueToPayItemNoGeometries?.records?.reduce(
      (map, { issueId, payItem }) =>
        map.set(issueId, [...(map.get(issueId) ?? []), payItem]),
      new Map<string, Issue['payItems']>()
    )
    const reports = relationsData?.issueToReports?.records?.reduce(
      (map, { issueId, ...report }) =>
        map.set(issueId, [...(map.get(issueId) ?? []), report]),
      new Map<string, Issue['reports']>()
    )
    setIssues(
      (issuesData?.issues?.records ?? []).map<Issue>((issue) => ({
        ...issue,
        comments: comments?.get(issue.id) ?? [],
        payItems: payItems?.get(issue.id) ?? [],
        reports: reports?.get(issue.id) ?? [],
      }))
    )
  }, [
    issuesData?.issues?.records,
    relationsData?.issueComments?.records,
    relationsData?.issueToPayItemNoGeometries?.records,
    relationsData?.issueToReports?.records,
  ])
  const reportUrlDeps = useReportURLDependencies()
  const [edit] = useEditIssueMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editIssue: {
        issue: { formattedNumber },
      },
    }) => simpleEditNotification(formattedNumber),
  })
  return (
    <Provider
      value={{
        quickLinkOptions: {
          quickLinks: relationsData?.issueQuickLinks?.records ?? [],
          loading: relationsLoading,
        },
        payItemGlobalId,
        issues: { loading: issuesLoading, refetch: refetchIssues, issues },
        relations: { loading: relationsLoading, refetch: refetchRelations },
        draftSubmitModal: useCIMSModalValues(),
        actionModal: useCIMSModalValues(),
        revertModal: useCIMSModalValues(),
        edit: async (input) => {
          await edit({ variables: { input } })
          refetchIssues()
          refetchRelations()
        },
        externalLinks: useExternalLinks<Issue>({
          parent: SharePointParentFolder.Issues,
          dataFn: ({
            externalLinks,
            number,
            id,
            sharePointResourceWebUrl,
            identifiedFieldIssue,
            reports,
          }) => {
            const prominentLinks = reports.map<ProminentLink>((report) =>
              toReportExternalLink(report, reportUrlDeps)
            )
            if (identifiedFieldIssue) {
              const { module, pathname } = CIMSPage.IFITracker
              prominentLinks.push({
                external: false,
                tooltip: getReportID(identifiedFieldIssue),
                label: 'Identified Field Issue',
                to: `/${projectId}/${module.pathname}/${pathname}/${identifiedFieldIssue.reportId}`,
              })
            }
            return {
              externalLinks,
              folder: number.toString(),
              recordId: id,
              resource: { webUrl: sharePointResourceWebUrl },
              prominentLinks,
            }
          },
          refetch: refetchIssues,
        }),
      }}
    >
      {children}
    </Provider>
  )
}

export default IssueTrackerProvider
